/**
 * DossierCountryPicker · edit the dossier's country in-place.
 *
 * The country drives the aids catalog (VaultAidsPanel) and the multi-
 * institution comparator.  Historically it could only be set at dossier
 * creation.  This picker exposes it on the dossier detail page so the
 * user (or advisor) can correct it after the fact — a must-have as we
 * roll out the catalog to Cameroon, Senegal, France, etc.
 *
 * Backend: PATCH /api/dossiers/{id} accepts {individual:{country:...}}
 * or {legal_entity:{country:...}} depending on the dossier type.
 */
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Globe2, Check, Loader2 } from "lucide-react";


export const COUNTRY_OPTIONS = [
  { code: "CA",   label: "🇨🇦 Canada" },
  { code: "US",   label: "🇺🇸 États-Unis" },
  { code: "FR",   label: "🇫🇷 France" },
  { code: "BE",   label: "🇧🇪 Belgique" },
  { code: "CH",   label: "🇨🇭 Suisse" },
  { code: "CM",   label: "🇨🇲 Cameroun" },
  { code: "SN",   label: "🇸🇳 Sénégal" },
  { code: "CI",   label: "🇨🇮 Côte d'Ivoire" },
  { code: "BJ",   label: "🇧🇯 Bénin" },
  { code: "MA",   label: "🇲🇦 Maroc" },
  { code: "TN",   label: "🇹🇳 Tunisie" },
  { code: "EU",   label: "🇪🇺 Europe (autre)" },
  { code: "INTL", label: "🌍 International / autre" },
];


export function labelForCountry(code) {
  return COUNTRY_OPTIONS.find((c) => c.code === (code || "").toUpperCase())?.label
      || (code ? `🌍 ${code}` : "🌍 Non renseigné");
}


export default function DossierCountryPicker({ dossier, onRefresh }) {
  const isIndividual = dossier?.type === "individual";
  const currentCountry = (
    isIndividual
      ? dossier?.individual?.country
      : dossier?.legal_entity?.country
  ) || "CA";

  const [country, setCountry] = useState(currentCountry.toUpperCase());
  const [saving, setSaving] = useState(false);
  const dirty = country !== currentCountry.toUpperCase();

  const save = async () => {
    setSaving(true);
    try {
      const payload = isIndividual
        ? { individual: { ...(dossier?.individual || {}), country } }
        : { legal_entity: { ...(dossier?.legal_entity || {}), country } };
      await api.patch(`/dossiers/${dossier.dossier_id}`, payload);
      toast.success(`Pays du dossier mis à jour → ${labelForCountry(country)}. Les aides sont recalculées.`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Modification du pays impossible.");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#E4E1D8] bg-white/70 px-3 py-2"
         data-testid="dossier-country-picker">
      <Globe2 className="h-4 w-4 text-[#7A5B14]" strokeWidth={2} />
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#5B5B62]">
        Pays du dossier
      </div>
      <select value={country} onChange={(e) => setCountry(e.target.value)}
              data-testid="dossier-country-select"
              className="rounded border border-[#E4E1D8] px-2 py-1 text-xs bg-white cursor-pointer">
        {COUNTRY_OPTIONS.map((c) => (
          <option key={c.code} value={c.code}>{c.label}</option>
        ))}
      </select>
      {dirty && (
        <button onClick={save} disabled={saving}
                data-testid="dossier-country-save"
                className="inline-flex items-center gap-1 rounded-md bg-[#0B2545] text-white hover:bg-[#123163] px-2.5 py-1 text-[11px]">
          {saving
            ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
            : <Check className="h-3 w-3" strokeWidth={2} />}
          Enregistrer
        </button>
      )}
      <div className="ml-auto text-[10px] font-mono text-[#8B8B8F]">
        Détermine les aides et les institutions comparées.
      </div>
    </div>
  );
}
